import { motion } from 'framer-motion'
import {
  Beaker,
  Package,
  ShieldCheck,
  ClipboardCheck,
  Factory,
  Layers,
  ArrowUpRight,
  type LucideIcon
} from 'lucide-react'

type Service = {
  title: string
  subtitle: string
  icon: LucideIcon
  bullets: string[]
  accent: 'orange' | 'pink' | 'gold'
}

const ACCENTS = {
  orange: { from: '#F47C20', to: '#E83E6F' },
  pink: { from: '#E83E6F', to: '#F47C20' },
  gold: { from: '#C9A227', to: '#E83E6F' }
}

const services: Service[] = [
  {
    title: 'Product Development & Customization',
    subtitle:
      'From sourcing and formulation to packaging and final specs — tailored to your brand vision.',
    icon: Beaker,
    accent: 'orange',
    bullets: [
      'Private Labeling (OEM & ODM)',
      'Customized Formulations (Herbal, Nutraceuticals, Superfoods)',
      'Specialized Packaging Designs and Branding'
    ]
  },
  {
    title: 'Quality Assurance & Compliance',
    subtitle:
      'Rigorous testing at every stage — from raw materials to final shipment for global standards.',
    icon: ShieldCheck,
    accent: 'gold',
    bullets: [
      'Certified Organic, GMP, HACCP, ISO Processes',
      'Third-party Laboratory Testing',
      'Full Regulatory Compliance for International Markets (USA, EU, Middle East, Asia)'
    ]
  },
  {
    title: 'Manufacturing & Scalability',
    subtitle:
      'Scalable operations for small batches to mass production — consistent quality at every level.',
    icon: Factory,
    accent: 'pink',
    bullets: [
      'Pilot Production Batches',
      'Bulk Manufacturing',
      'Custom Contract Manufacturing'
    ]
  }
]

const sectionReveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
}

const cardReveal = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55 } }
}

export default function TurnkeyServices () {
  return (
    <section className='relative bg-white py-18 md:py-24 overflow-hidden '>
      {/* Ambient glows */}
      <div className='pointer-events-none absolute -top-28 -left-28 h-80 w-80 rounded-full blur-3xl opacity-10 bg-[#F47C20]' />
      <div className='pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full blur-3xl opacity-10 bg-[#E83E6F]' />

      <div className='max-w-7xl mx-auto px-6 md:px-8'>
        {/* Header */}
        <motion.div
          variants={sectionReveal}
          initial='hidden'
          whileInView='show'
          viewport={{ once: true, amount: 0.25 }}
          className='max-w-3xl mx-auto text-center'
        >
          <div className='inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.02] px-4 py-2'>
            <Layers size={16} className='text-[#C62828]' />
            <span className='text-xs font-semibold tracking-[0.22em] uppercase text-[#2B2B2B]/80 '>
              Services
            </span>
          </div>

          <h2 className='mt-6 text-3xl md:text-5xl font-extrabold leading-tight text-[#1B1B1B]'>
            Our Full{' '}
            <span className='bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent'>
              Turn-key Services
            </span>{' '}
            Offerings
          </h2>

          <p className='mt-5 text-base md:text-lg text-[#2B2B2B]/80 leading-relaxed'>
            End-to-end contract manufacturing support designed for global brands
            — from development and compliance to scalable production and
            export-ready delivery.
          </p>
        </motion.div>

        {/* Cards */}
        <div className='mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {services.map(s => {
            const Icon = s.icon
            const accent = ACCENTS[s.accent]

            return (
              <motion.div
                key={s.title}
                variants={cardReveal}
                initial='hidden'
                whileInView='show'
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -8 }}
                className='group relative rounded-2xl border border-black/10 bg-white shadow-sm hover:shadow-lg transition overflow-hidden'
              >
                {/* Accent top bar */}
                <div
                  className='h-[4px] w-full'
                  style={{
                    background: `linear-gradient(90deg, ${accent.from}, ${accent.to})`
                  }}
                />

                {/* Soft accent glow */}
                <div
                  className='pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition duration-300'
                  style={{ background: accent.from }}
                />

                <div className='p-6 text-left'>
                  {/* Icon + title row */}
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                      <div
                        className='h-12 w-12 rounded-xl p-[1px]'
                        style={{
                          background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`
                        }}
                      >
                        <div className='h-full w-full rounded-xl bg-white flex items-center justify-center'>
                          <Icon size={22} className='text-[#C62828]' />
                        </div>
                      </div>

                      <div>
                        <h3 className='text-base font-semibold text-[#1B1B1B]'>
                          {s.title}
                        </h3>
                        <div className='mt-1 flex items-center gap-2 text-xs text-[#2B2B2B]/60'>
                          <ClipboardCheck size={14} />
                          End-to-end support
                        </div>
                      </div>
                    </div>

                    <ArrowUpRight
                      size={18}
                      className='text-[#2B2B2B]/50 group-hover:text-[#E83E6F] transition'
                    />
                  </div>

                  {/* Subtitle */}
                  <p className='mt-4 text-sm leading-6 text-[#2B2B2B]/75'>
                    {s.subtitle}
                  </p>

                  {/* Bullets */}
                  {/* <div className="mt-5 space-y-3">
                    {s.bullets.map((b) => (
                      <div key={b} className="flex items-start gap-3">
                        <span
                          className="mt-1.5 h-2 w-2 rounded-full"
                          style={{
                            background: `linear-gradient(90deg, ${accent.from}, ${accent.to})`,
                          }}
                        />
                        <p className="text-sm text-[#2B2B2B]/80">{b}</p>
                      </div>
                    ))}
                  </div> */}
                  {/* Bullets */}
                  <div className='mt-5 space-y-3'>
                    {s.bullets.map(b => (
                      <div key={b} className='flex items-start gap-3'>
                        {/* Dot */}
                        <span
                          className='mt-[7px] h-2 w-2 rounded-full shrink-0'
                          style={{
                            background: `linear-gradient(90deg, ${accent.from}, ${accent.to})`
                          }}
                        />
                        {/* Text */}
                        <p className='text-sm leading-6 text-[#2B2B2B]/80'>
                          {b}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Bottom mini CTA */}
                  <div className='mt-6 flex items-center justify-between'>
                    <div className='flex items-center gap-2 text-xs text-[#2B2B2B]/60'>
                      <Package size={14} />
                      Export-ready delivery
                    </div>

                    <a
                      href='/contact'
                      className='text-sm font-semibold text-[#C62828] hover:text-[#F47C20] transition'
                    >
                      Learn more
                    </a>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Bottom CTA bar */}
        <motion.div
          variants={sectionReveal}
          initial='hidden'
          whileInView='show'
          viewport={{ once: true, amount: 0.2 }}
          className='mt-10 rounded-2xl border border-black/10 bg-gradient-to-r from-[#FFF8F3] to-white p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4'
        >
          <div>
            <p className='text-sm font-semibold text-[#1B1B1B]'>
              Want a custom quote for your brand?
            </p>
            <p className='mt-1 text-sm text-[#2B2B2B]/75'>
              Share your product requirements and we’ll recommend the best
              manufacturing path.
            </p>
          </div>

          <motion.a
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            href='/contact'
            className='inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold text-white
                       bg-gradient-to-r from-[#F47C20] to-[#E83E6F] shadow-md shadow-black/10 hover:opacity-95 transition'
          >
            Request Quote
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}
